import { useRef } from "react";
import { Form } from "react-bootstrap";
import { PERIOD_OPTIONS, PeriodKey } from "../../../../consts";
import {
  formatYMD,
  subtractDays,
  subtractMonths,
  subtractYears,
} from "../../../../utils";
import "./index.scss";

export interface OnPeriodChangePayload {
  period: PeriodKey;
  startDate: string | null;
  endDate: string | null;
}

interface PeriodSelectorProps {
  onPeriodChange?: (data: OnPeriodChangePayload) => void;
  period: OnPeriodChangePayload;
}

const PeriodSelector = ({ onPeriodChange, period }: PeriodSelectorProps) => {
  const today = formatYMD(new Date());

  const selectedPeriod = period.period || "always";

  const startRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLInputElement | null>(null);

  const periodOptions: PeriodKey[] = [
    "30_days",
    "6_months",
    "12_months",
    "2_years",
    "5_years",
    "always",
    "custom",
  ];

  const handlePeriodSelect = (periodKey: PeriodKey) => {
    let newStart: string | null = null;
    let newEnd: string | null = null;

    if (periodKey === "30_days") {
      newEnd = today;
      newStart = formatYMD(subtractDays(new Date(), 30));
    }

    if (periodKey === "12_months") {
      newEnd = today;
      newStart = formatYMD(subtractMonths(new Date(), 12));
    }

    if (periodKey === "6_months") {
      newEnd = today;
      newStart = formatYMD(subtractMonths(new Date(), 6));
    }

    if (periodKey === "2_years") {
      newEnd = today;
      newStart = formatYMD(subtractYears(new Date(), 2));
    }

    if (periodKey === "5_years") {
      newEnd = today;
      newStart = formatYMD(subtractYears(new Date(), 5));
    }

    if (periodKey === "always") {
      newStart = null;
      newEnd = null;
    }

    if (periodKey === "custom") {
      newStart = today;
      newEnd = today;
    }

    onPeriodChange?.({
      period: periodKey,
      startDate: newStart,
      endDate: newEnd,
    });
  };

  const handleDateChange = (type: "start" | "end", value: string) => {
    if (type === "start") {
      onPeriodChange?.({
        period: "custom",
        startDate: value,
        endDate: period.endDate,
      });
    } else {
      onPeriodChange?.({
        period: "custom",
        startDate: period.startDate,
        endDate: value,
      });
    }
  };

  const formatDisplay = (dateString: string | null): string => {
    if (!dateString) return "";
    const [y, m, d] = dateString.split("-");
    const date = new Date(Number(y), Number(m) - 1, Number(d));

    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  };

  return (
    <div className="period-selector">
      <div className="period-options-list">
        {periodOptions.map((key) => (
          <div
            key={key}
            className={`period-option-item ${selectedPeriod === key ? "selected" : ""}`}
            onClick={() => handlePeriodSelect(key)}
          >
            {selectedPeriod === key && <span className="checkmark">✓</span>}
            <span>{PERIOD_OPTIONS[key].title}</span>
          </div>
        ))}
      </div>

      {selectedPeriod === "custom" && (
        <div className="custom-date-inputs">
          <div className="date-input-wrapper">
            <Form.Control
              type="date"
              ref={startRef}
              value={period.startDate ?? ""}
              onChange={(e) => handleDateChange("start", e.target.value)}
              className="date-input"
            />
          </div>

          <span className="date-separator">-</span>

          <div className="date-input-wrapper">
            <Form.Control
              type="date"
              ref={endRef}
              value={period.endDate ?? ""}
              onChange={(e) => handleDateChange("end", e.target.value)}
              className="date-input"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodSelector;
